import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Fab,
  useMediaQuery,
  Paper,
  Typography,
  useTheme,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tab,
  Tabs,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";

import PageContainer from "../components/PageContainer";
import { phoneMatchesSearch } from "../utils/phoneUtils";
import PageSection from "../components/PageSection";
import { SearchField } from "../components/shared";
import { TeamList, TeamManagementSkeleton } from "../components/TeamManagement";
import TeamCertifications from "../components/TeamManagement/TeamCertifications";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
  DeleteModal,
} from "../components/shared";
import { useTeamMembers } from "../hooks/useTeamMembers";
import { useCircles } from "../hooks/useCircles";
import AddTeamMember from "../components/AddTeamMember";
import { useRegisterPageActions } from "../context/PageActionsContext";
import { lightTheme } from "../styles/theme";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import {
  MANAGEMENT_ADD_MORE_BUTTON_SX,
  MANAGEMENT_MOBILE_FAB_SX,
  mobileFabWithScrollVisibility,
} from "../styles/managementShared";
import { useMobileFabVisibility } from "../hooks/useMobileFabVisibility";
import { TeamMember } from "../services/api";
import { getMaxTeamMembers, isUnlimitedTierLimit } from "../utils/tierLimits";
import VideoPlayerModal from "../components/VideoPlayerModal";
import { readTeamHandover, TeamMemberPrefill } from "../domain/team/teamHandover";
import { isAdminMember } from "../domain/team/teamRoles";
import { FAMILY_TAB_SX } from "../components/UserManagement/Family/familyStyles";

type TeamTab = "members" | "certifications";

const EDIT_TEAM_MEMBER_FORM_ID = "edit-team-member-form";
const ACCESS_LEVEL_FORM_TO_LABEL: Record<
  "CREATOR" | "OWNER" | "ADMIN",
  string
> = {
  CREATOR: "Creator",
  OWNER: "Owner",
  ADMIN: "Admin",
};

/** Compare team login ids ignoring @suffix (OID vs soid in UI). */
function teamMemberUsernameLocalPart(u: string | undefined): string {
  if (!u || typeof u !== "string") return "";
  const t = u.trim().toLowerCase();
  return t.includes("@") ? (t.split("@")[0] ?? "") : t;
}

interface ManageTeamProps {
  hideHeader?: boolean;
}

const ManageTeam: React.FC<ManageTeamProps> = ({ hideHeader = false }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberPrefill, setAddMemberPrefill] = useState<TeamMemberPrefill>();
  /** A team member handed over to add, opened once the Team has loaded so the tier limit is known. */
  const [handedOverMember, setHandedOverMember] = useState<TeamMemberPrefill>();
  const [activeFilter, setActiveFilter] = useState<"all" | "admin" | "mobile">(
    "all"
  );
  const [editMemberOpen, setEditMemberOpen] = useState(false);
  const [deleteMemberOpen, setDeleteMemberOpen] = useState(false);
  const [resetPinOpen, setResetPinOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    accessLevel: "CREATOR" as "CREATOR" | "OWNER" | "ADMIN",
    phoneNumber: "",
    circleIds: [] as string[],
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResettingPin, setIsResettingPin] = useState(false);
  const [resetPinResult, setResetPinResult] = useState<{
    pin: string;
    deliveryMethods: string[];
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    videoPath: string;
    title: string;
  } | null>(null);

  const theme = useTheme();
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const teamTab: TeamTab =
    searchParams.get("tab") === "certifications" ? "certifications" : "members";

  const setTeamTab = (next: TeamTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === "certifications") params.set("tab", "certifications");
    else params.delete("tab");
    setSearchParams(params, { replace: true });
  };

  /** Align with AddTeamMember / InviteUsers modal fields (typography + 16px text on mobile). */
  const editModalFieldSx = useMemo(() => {
    const ff = theme.typography.fontFamily;
    const fontFamily =
      ff == null ? "inherit" : Array.isArray(ff) ? ff.join(", ") : ff;
    return {
      fontFamily,
      "& .MuiOutlinedInput-input": {
        fontSize: { xs: "1rem", sm: "0.875rem" },
      },
      "& .MuiSelect-select": {
        fontSize: { xs: "1rem", sm: "0.875rem" },
      },
    };
  }, [theme]);

  const {
    data: teamMembers = [],
    isLoading,
    refetch,
    error,
  } = useTeamMembers();
  const { data: circles = [], isLoading: circlesLoading } = useCircles();
  const isMdUp = useMediaQuery("(min-width:900px)");
  const isMobile = useMediaQuery("(max-width:599px)");
  const mobileFabVisible = useMobileFabVisibility(
    isMobile && !hideHeader && teamTab === "members",
  );
  const mobileFabSx = useMemo(
    () => mobileFabWithScrollVisibility(MANAGEMENT_MOBILE_FAB_SX, mobileFabVisible),
    [mobileFabVisible],
  );

  // Memoized filtered team members with search and filter logic
  const filteredTeamMembers = useMemo(() => {
    let filtered = teamMembers;

    // Apply active filter
    if (activeFilter === "admin") {
      filtered = filtered.filter((member) => isAdminMember(member.accessLevel));
    } else if (activeFilter === "mobile") {
      filtered = filtered.filter(
        (member) => member.phoneNumber && member.phoneNumber.trim() !== ""
      );
    }

    // Apply search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (member) =>
          member.displayName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          member.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
          phoneMatchesSearch(member.phoneNumber, searchQuery)
      );
    }

    return filtered;
  }, [teamMembers, searchQuery, activeFilter]);

  // Check if user has reached tier limit
  const userTier = user?.tier || "0";
  const maxTeamMembers = getMaxTeamMembers(userTier);
  const hasReachedLimit =
    !isUnlimitedTierLimit(maxTeamMembers) &&
    teamMembers.length >= maxTeamMembers;
  const limitMessage = hasReachedLimit
    ? `You've reached your tier limit of ${maxTeamMembers} team members. Upgrade your plan to add more members.`
    : "";

  // Memoized handlers
  const handleOpenAddMember = useCallback(() => {
    if (hasReachedLimit) {
      setIsUpgradeModalOpen(true);
      return;
    }
    setAddMemberOpen(true);
  }, [hasReachedLimit]);

  const handleCloseAddMember = useCallback(() => {
    setAddMemberOpen(false);
    setAddMemberPrefill(undefined);
  }, []);

  const handleAddMemberSuccess = useCallback(() => {
    setAddMemberOpen(false);
    setAddMemberPrefill(undefined);
    refetch();
  }, [refetch]);

  // Another page (Ask Noticeboard) can open Team searched, or with a team member filled in.
  // Read once, then cleared, so a reload or Back never opens it again.
  useEffect(() => {
    const { search, addMember } = readTeamHandover(location.state);
    if (!search && !addMember) return;
    if (search) setSearchQuery(search);
    if (addMember) setHandedOverMember(addMember);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!handedOverMember || isLoading) return;
    setHandedOverMember(undefined);
    // At the tier limit this shows the upgrade instead, and the details are dropped
    if (!hasReachedLimit) setAddMemberPrefill(handedOverMember);
    handleOpenAddMember();
  }, [handedOverMember, isLoading, hasReachedLimit, handleOpenAddMember]);

  const handleFilterClick = useCallback(
    (filter: "all" | "admin" | "mobile") => {
      setActiveFilter(filter);
      setSearchQuery(""); // Clear search when applying filter
    },
    []
  );

  // Same logic as AddTeamMember — case-insensitive; team Admin can assign Owner/Creator
  const availableAccessLevels = useMemo(() => {
    if (user?.userType !== "team") {
      return [
        { value: "CREATOR", label: "Creator" },
        { value: "OWNER", label: "Owner" },
        { value: "ADMIN", label: "Admin" },
      ];
    }
    const level = (user?.accessLevel || "").trim().toLowerCase();
    if (level === "admin") {
      return [
        { value: "CREATOR", label: "Creator" },
        { value: "OWNER", label: "Owner" },
      ];
    }
    if (level === "owner") {
      return [{ value: "CREATOR", label: "Creator" }];
    }
    return [];
  }, [user?.accessLevel, user?.userType]);

  // Subcircles the current user may assign when editing (team = intersection with JWT circleIds)
  const circlesScopedToRequester = useMemo(() => {
    const sub = circles.filter((c) => c.circleId !== user?.schoolId);
    if (user?.userType !== "team") {
      return sub;
    }
    const level = (user?.accessLevel || "").trim().toLowerCase();
    if (level === "owner" || level === "admin") {
      return sub;
    }
    const allowed = user?.circleIds;
    if (!Array.isArray(allowed) || allowed.length === 0) {
      return [];
    }
    const allow = new Set(allowed.map(String));
    return sub.filter((c) => allow.has(c.circleId));
  }, [circles, user?.schoolId, user?.userType, user?.circleIds, user?.accessLevel]);

  // Edit member handler
  const handleEditMember = useCallback(
    (member: TeamMember) => {
      setSelectedMember(member);
      // Normalize access level from API format (e.g., "Creator") to form format (e.g., "CREATOR")
      const normalizeAccessLevel = (
        level: string
      ): "CREATOR" | "OWNER" | "ADMIN" => {
        const upperLevel = level.toUpperCase();
        if (
          upperLevel === "CREATOR" ||
          upperLevel === "OWNER" ||
          upperLevel === "ADMIN"
        ) {
          return upperLevel as "CREATOR" | "OWNER" | "ADMIN";
        }
        return "CREATOR"; // Default fallback
      };

      let initialCircleIds = member.circleIds || [];
      const requesterLevel = (user?.accessLevel || "").trim().toLowerCase();
      if (
        user?.userType === "team" &&
        requesterLevel !== "owner" &&
        requesterLevel !== "admin" &&
        Array.isArray(user.circleIds)
      ) {
        const allow = new Set(user.circleIds.map(String));
        initialCircleIds = initialCircleIds.filter((id) => allow.has(id));
      }

      setEditFormData({
        displayName: member.displayName,
        accessLevel: normalizeAccessLevel(member.accessLevel),
        phoneNumber: member.phoneNumber || "",
        circleIds: initialCircleIds,
      });
      setEditMemberOpen(true);
      setErrorMessage(null);
    },
    [user?.userType, user?.circleIds, user?.accessLevel]
  );

  const isEditingOwnTeamProfile = useMemo(() => {
    if (!editMemberOpen || user?.userType !== "team" || !selectedMember) {
      return false;
    }
    const memberLocal = teamMemberUsernameLocalPart(selectedMember.username);
    const sessionLocal = teamMemberUsernameLocalPart(
      typeof user.username === "string"
        ? user.username
        : typeof user.id === "string"
          ? user.id
          : ""
    );
    return memberLocal.length > 0 && memberLocal === sessionLocal;
  }, [editMemberOpen, user?.userType, user?.username, user?.id, selectedMember]);

  const canDeleteMember = useCallback(
    (member: TeamMember) => {
      if (user?.userType !== "team") {
        return true;
      }
      const requesterLevel = String(user?.accessLevel || "")
        .trim()
        .toLowerCase();
      const targetLevel = String(member.accessLevel || "")
        .trim()
        .toLowerCase();
      const currentTeamLocal = teamMemberUsernameLocalPart(
        typeof user?.username === "string"
          ? user.username
          : typeof user?.id === "string"
            ? user.id
            : ""
      );
      const targetLocal = teamMemberUsernameLocalPart(member.username);
      if (currentTeamLocal.length > 0 && currentTeamLocal === targetLocal) {
        return false;
      }
      if (requesterLevel === "owner" && targetLevel === "owner") {
        return false;
      }
      return true;
    },
    [user?.id, user?.userType, user?.username, user?.accessLevel]
  );

  const handleCloseEditMember = useCallback(() => {
    setEditMemberOpen(false);
    setSelectedMember(null);
    setErrorMessage(null);
  }, []);

  const handleUpdateMember = useCallback(async () => {
    if (!selectedMember || !user?.schoolId || !token) return;

    setIsUpdating(true);
    setErrorMessage(null);

    try {
      const payload: {
        displayName: string;
        accessLevel?: "CREATOR" | "OWNER" | "ADMIN";
        phoneNumber?: string;
        circleIds: string[];
      } = {
        displayName: editFormData.displayName,
        phoneNumber: editFormData.phoneNumber || undefined,
        circleIds: editFormData.circleIds,
      };
      if (!isEditingOwnTeamProfile) {
        payload.accessLevel = editFormData.accessLevel;
      }

      await api.team.updateTeamMember(
        user.schoolId,
        selectedMember.username,
        payload,
        token
      );

      handleCloseEditMember();
      refetch();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to update team member"
      );
    } finally {
      setIsUpdating(false);
    }
  }, [
    selectedMember,
    editFormData,
    user?.schoolId,
    token,
    refetch,
    handleCloseEditMember,
    isEditingOwnTeamProfile,
  ]);

  // Delete member handler
  const handleDeleteMember = useCallback((member: TeamMember) => {
    const requesterLevel = String(user?.accessLevel || "")
      .trim()
      .toLowerCase();
    const targetLevel = String(member.accessLevel || "")
      .trim()
      .toLowerCase();
    const currentTeamLocal = teamMemberUsernameLocalPart(
      typeof user?.username === "string"
        ? user.username
        : typeof user?.id === "string"
          ? user.id
          : ""
    );
    const targetLocal = teamMemberUsernameLocalPart(member.username);
    if (
      user?.userType === "team" &&
      currentTeamLocal.length > 0 &&
      currentTeamLocal === targetLocal
    ) {
      setErrorMessage("You cannot remove your own team account.");
      return;
    }
    if (
      user?.userType === "team" &&
      requesterLevel === "owner" &&
      targetLevel === "owner"
    ) {
      setErrorMessage("Owners cannot remove other owners.");
      return;
    }
    setSelectedMember(member);
    setDeleteMemberOpen(true);
    setErrorMessage(null);
  }, [user?.id, user?.userType, user?.username, user?.accessLevel]);

  const handleCloseDeleteMember = useCallback(() => {
    setDeleteMemberOpen(false);
    setSelectedMember(null);
    setErrorMessage(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedMember || !user?.schoolId || !token) return;

    setIsDeleting(true);
    setErrorMessage(null);

    try {
      await api.team.deleteTeamMember(
        user.schoolId,
        selectedMember.username,
        token
      );

      handleCloseDeleteMember();
      refetch();
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to remove team member"
      );
    } finally {
      setIsDeleting(false);
    }
  }, [selectedMember, user?.schoolId, token, refetch, handleCloseDeleteMember]);

  // Reset PIN handler
  const handleResetPin = useCallback((member: TeamMember) => {
    setSelectedMember(member);
    setResetPinOpen(true);
    setResetPinResult(null);
    setErrorMessage(null);
  }, []);

  const handleCloseResetPin = useCallback(() => {
    setResetPinOpen(false);
    setSelectedMember(null);
    setResetPinResult(null);
    setErrorMessage(null);
  }, []);

  const handleConfirmResetPin = useCallback(async () => {
    if (!selectedMember || !user?.schoolId || !token) return;

    setIsResettingPin(true);
    setErrorMessage(null);

    try {
      const result = await api.team.resetTeamMemberPin(
        user.schoolId,
        selectedMember.username,
        token
      );

      setResetPinResult({
        pin: result.pin,
        deliveryMethods: result.deliveryMethods,
      });
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to reset PIN"
      );
    } finally {
      setIsResettingPin(false);
    }
  }, [selectedMember, user?.schoolId, token]);

  // Register page actions with the unified navigation
  const pageActions = useMemo(
    () =>
      teamTab === "certifications"
        ? {}
        : {
            onMainAction: handleOpenAddMember,
            onVideoPlay: () =>
              setSelectedVideo({
                videoPath: "/videos/adduser.webm",
                title: "How to Add Team Members",
              }),
            count: filteredTeamMembers.length,
          },
    [handleOpenAddMember, filteredTeamMembers.length, teamTab]
  );

  useRegisterPageActions(pageActions);

  // Calculate team statistics
  const teamStats = useMemo(() => {
    const totalMembers = teamMembers.length;
    const adminMembers = teamMembers.filter((member) =>
      isAdminMember(member.accessLevel)
    ).length;
    const mobileMembers = teamMembers.filter(
      (member) => member.phoneNumber && member.phoneNumber.trim() !== ""
    ).length;

    return {
      totalMembers,
      adminMembers,
      mobileMembers,
    };
  }, [teamMembers]);

  const content = (
    <>
      {error && teamTab === "members" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load team members. Please try refreshing the page.
        </Alert>
      )}

      {!hideHeader && (
        <>
          <Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={teamTab}
              onChange={(_event, next: TeamTab) => setTeamTab(next)}
              aria-label="Team sections"
              variant="scrollable"
              allowScrollButtonsMobile
              sx={FAMILY_TAB_SX}
            >
              <Tab
                value="members"
                label="Members"
                id="team-tab-members"
                aria-controls="team-tabpanel-members"
              />
              <Tab
                value="certifications"
                label="Certifications"
                id="team-tab-certifications"
                aria-controls="team-tabpanel-certifications"
              />
            </Tabs>
          </Box>
          {teamTab === "certifications" ? (
            <Box
              role="tabpanel"
              id="team-tabpanel-certifications"
              aria-labelledby="team-tab-certifications"
            >
              <TeamCertifications />
            </Box>
          ) : isLoading ? (
            <TeamManagementSkeleton />
          ) : (
            <Box
              role="tabpanel"
              id="team-tabpanel-members"
              aria-labelledby="team-tab-members"
            >
              {/* Stats Overview */}
              {teamMembers.length > 0 && (
                <PageSection>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, md: 4 },
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                        boxShadow: "none",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: { xs: 3, md: 5 },
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            textAlign: "center",
                            cursor: "pointer",
                            borderRadius: 2,
                            p: 1,
                            transition: "all 0.2s ease",
                            backgroundColor:
                              activeFilter === "all"
                                ? alpha(theme.palette.primary.main, 0.05)
                                : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              transform: "translateY(-2px)",
                            },
                          }}
                          onClick={() => handleFilterClick("all")}
                        >
                          <Typography
                            variant="h3"
                            sx={{
                              color: lightTheme.colors.primary,
                              fontWeight: 700,
                              mb: 0.5,
                              fontSize: { xs: "2rem", md: "2.5rem" },
                            }}
                          >
                            {teamStats.totalMembers}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                              letterSpacing: "0.5px",
                            }}
                          >
                            Total Members
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            height: { xs: 50, md: 60 },
                            width: 2,
                            background: `linear-gradient(180deg, transparent 0%, ${lightTheme.colors.primary}40 50%, transparent 100%)`,
                            borderRadius: 1,
                          }}
                        />

                        <Box
                          sx={{
                            textAlign: "center",
                            cursor: "pointer",
                            borderRadius: 2,
                            p: 1,
                            transition: "all 0.2s ease",
                            backgroundColor:
                              activeFilter === "admin"
                                ? alpha(theme.palette.primary.main, 0.05)
                                : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              transform: "translateY(-2px)",
                            },
                          }}
                          onClick={() => handleFilterClick("admin")}
                        >
                          <Typography
                            variant="h3"
                            sx={{
                              color: lightTheme.colors.primary,
                              fontWeight: 700,
                              mb: 0.5,
                              fontSize: { xs: "2rem", md: "2.5rem" },
                            }}
                          >
                            {teamStats.adminMembers}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "text.secondary",
                              fontWeight: 500,
                              letterSpacing: "0.5px",
                            }}
                          >
                            Admin Members
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </PageSection>
              )}

              {/* Tier Limit Alert */}
              {hasReachedLimit && (
                <PageSection sx={{ mb: 2 }}>
                  <Alert
                    severity="warning"
                    sx={{
                      backgroundColor: (theme) =>
                        alpha(theme.palette.warning.main, 0.16),
                      border: (theme) =>
                        `1px solid ${theme.palette.warning.main}`,
                      "& .MuiAlert-icon": {
                        color: "warning.dark",
                      },
                      "& .MuiAlert-message": {
                        color: "warning.dark",
                      },
                    }}
                    action={
                      <NoticeboardButton
                        variant="primary"
                        size="small"
                        onClick={() => setIsUpgradeModalOpen(true)}
                        sx={{
                          bgcolor: lightTheme.colors.accent,
                          border: "none",
                          "&:hover": {
                            bgcolor: theme.palette.warning.dark,
                          },
                        }}
                      >
                        Upgrade
                      </NoticeboardButton>
                    }
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Tier Limit Reached
                    </Typography>
                    <Typography variant="body2">
                      {limitMessage}
                    </Typography>
                  </Alert>
                </PageSection>
              )}

              {/* Search Section */}
              {teamMembers.length > 0 && (
                <PageSection sx={{ mb: 2 }}>
                  <Box sx={{ mb: 3 }}>
                    <SearchField
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search team members by name, username, or phone..."
                      sx={{ width: "100%" }}
                    />
                  </Box>
                </PageSection>
              )}
              <TeamList
                teamMembers={filteredTeamMembers}
                circles={circles}
                onDeleteMember={handleDeleteMember}
                canDeleteMember={canDeleteMember}
                onEditMember={handleEditMember}
                onResetPin={handleResetPin}
                onAddMember={handleOpenAddMember}
                isMobile={!isMdUp}
                isLoading={isLoading || circlesLoading}
              />
              {filteredTeamMembers.length > 0 && !isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                    pt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleOpenAddMember}
                    disabled={hasReachedLimit}
                    sx={MANAGEMENT_ADD_MORE_BUTTON_SX}
                  >
                    + Add More Team Members
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      <AddTeamMember
        open={addMemberOpen}
        onClose={handleCloseAddMember}
        onSuccess={handleAddMemberSuccess}
        onMemberAdded={refetch}
        prefill={addMemberPrefill}
      />

      {/* Edit Member Modal */}
      <ActionModal
        open={editMemberOpen}
        onClose={handleCloseEditMember}
        title="Edit Team Member"
        subtitle="Update team member information and access settings"
        maxWidth="md"
        headerStyle="light"
        denseContent
        contentSx={{ px: { xs: 2.5 } }}
        footerSx={{ px: { xs: 2.5 } }}
        disableBackdropClick={isUpdating}
        footer={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              width: "100%",
              justifyContent: "flex-end",
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <NoticeboardButton
              type="submit"
              form={EDIT_TEAM_MEMBER_FORM_ID}
              variant="primary"
              size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
              disabled={isUpdating || !editFormData.displayName.trim()}
              loading={isUpdating}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 200 },
                borderRadius: 1,
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.primary.main, 0.35),
                  color: alpha(theme.palette.common.white, 0.92),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              Update
            </NoticeboardButton>
          </Box>
        }
      >
        <Box
          component="form"
          id={EDIT_TEAM_MEMBER_FORM_ID}
          onSubmit={(e) => {
            e.preventDefault();
            void handleUpdateMember();
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            width: "100%",
            minWidth: 0,
            pt: { xs: 1, sm: 1.5 },
          }}
        >
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 0 }}>
              {errorMessage}
            </Alert>
          )}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                letterSpacing: "0.02em",
                mb: 2,
              }}
            >
              Team member
            </Typography>
            <TextField
              label="Display Name"
              value={editFormData.displayName}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  displayName: e.target.value,
                })
              }
              fullWidth
              required
              sx={editModalFieldSx}
            />
          </Box>
          <FormControl fullWidth sx={editModalFieldSx}>
            <InputLabel>Access Level</InputLabel>
            {isEditingOwnTeamProfile ? (
              <Select
                value={editFormData.accessLevel}
                label="Access Level"
                disabled
                displayEmpty
              >
                <MenuItem value={editFormData.accessLevel}>
                  {ACCESS_LEVEL_FORM_TO_LABEL[editFormData.accessLevel]}
                </MenuItem>
              </Select>
            ) : (
              <Select
                value={editFormData.accessLevel}
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    accessLevel: e.target.value as
                      | "CREATOR"
                      | "OWNER"
                      | "ADMIN",
                  })
                }
                label="Access Level"
              >
                {availableAccessLevels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
          {isEditingOwnTeamProfile && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Your access level can only be changed by an organization administrator.
            </Typography>
          )}
          <TextField
            label="Phone Number"
            value={editFormData.phoneNumber}
            onChange={(e) =>
              setEditFormData({
                ...editFormData,
                phoneNumber: e.target.value,
              })
            }
            fullWidth
            sx={editModalFieldSx}
          />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: "text.secondary",
                fontSize: "0.8125rem",
                letterSpacing: "0.02em",
              }}
            >
              Circle Access
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {editFormData.accessLevel === "OWNER" && (
                <Chip
                  label="All Circles"
                  onClick={() => {
                    const allCircleIds = circlesScopedToRequester.map(
                      (circle) => circle.circleId
                    );
                    const allSelected =
                      allCircleIds.length > 0 &&
                      editFormData.circleIds.length === allCircleIds.length;
                    setEditFormData({
                      ...editFormData,
                      circleIds: allSelected ? [] : allCircleIds,
                    });
                  }}
                  color={
                    circlesScopedToRequester.length > 0 &&
                    editFormData.circleIds.length === circlesScopedToRequester.length
                      ? "primary"
                      : "default"
                  }
                  variant={
                    circlesScopedToRequester.length > 0 &&
                    editFormData.circleIds.length === circlesScopedToRequester.length
                      ? "filled"
                      : "outlined"
                  }
                />
              )}
              {circlesScopedToRequester.map((circle) => (
                  <Chip
                    key={circle.circleId}
                    label={circle.name}
                    onClick={() => {
                      const isSelected = editFormData.circleIds.includes(
                        circle.circleId
                      );
                      setEditFormData({
                        ...editFormData,
                        circleIds: isSelected
                          ? editFormData.circleIds.filter(
                              (id) => id !== circle.circleId
                            )
                          : [...editFormData.circleIds, circle.circleId],
                      });
                    }}
                    color={
                      editFormData.circleIds.includes(circle.circleId)
                        ? "primary"
                        : "default"
                    }
                    variant={
                      editFormData.circleIds.includes(circle.circleId)
                        ? "filled"
                        : "outlined"
                    }
                  />
                ))}
            </Box>
          </Box>
        </Box>
      </ActionModal>

      {/* Delete Member Confirmation Modal */}
      <DeleteModal
        open={deleteMemberOpen}
        onClose={handleCloseDeleteMember}
        title="Confirm Removal"
        subtitle="This action cannot be undone"
      >
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Typography
          variant="body1"
          sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}
        >
          Are you sure you want to remove the team member{" "}
          <Typography component="span" fontWeight={600}>
            "{selectedMember?.displayName}"
          </Typography>
          ? This action cannot be undone and all team member data will be
          permanently removed.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <NoticeboardButton
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Removing..." : "Remove Team Member"}
          </NoticeboardButton>
        </Box>
      </DeleteModal>

      {/* Reset PIN Modal */}
      <ActionModal
        open={resetPinOpen}
        onClose={handleCloseResetPin}
        title="Reset PIN"
        subtitle={
          resetPinResult
            ? "PIN has been reset successfully"
            : "Generate a new PIN for this team member"
        }
        maxWidth="md"
        headerStyle="light"
      >
        <Box sx={{ p: 3 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {resetPinResult ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              <Alert severity="success" sx={{ mb: 2 }}>
                PIN reset successfully!
              </Alert>
              <Box
                sx={{
                  p: 3,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  New PIN
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    fontFamily: "monospace",
                    letterSpacing: 2,
                  }}
                >
                  {resetPinResult.pin}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Delivery methods: {resetPinResult.deliveryMethods.join(", ")}
              </Typography>
              <Alert severity="info">
                Please share this PIN with the team member securely. The PIN has
                been sent via {resetPinResult.deliveryMethods.join(" and ")}.
              </Alert>
              <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                <NoticeboardButton
                  variant="primary"
                  onClick={handleCloseResetPin}
                >
                  Close
                </NoticeboardButton>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "1rem", lineHeight: 1.6 }}
              >
                Are you sure you want to reset the PIN for{" "}
                <Typography component="span" fontWeight={600}>
                  "{selectedMember?.displayName}"
                </Typography>
                ? A new PIN will be generated and sent to the team member.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  pt: 2,
                  justifyContent: "center",
                }}
              >
                <NoticeboardButton
                  variant="secondary"
                  onClick={handleCloseResetPin}
                  sx={{ flex: 1 }}
                >
                  Cancel
                </NoticeboardButton>
                <NoticeboardButton
                  variant="primary"
                  onClick={handleConfirmResetPin}
                  disabled={isResettingPin}
                  sx={{ flex: 1 }}
                >
                  {isResettingPin ? "Resetting..." : "Reset PIN"}
                </NoticeboardButton>
              </Box>
            </Box>
          )}
        </Box>
      </ActionModal>

      <ActionModal
        open={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title="Upgrade Your Plan"
        subtitle="Contact Noticeboard support to upgrade your subscription"
        maxWidth="sm"
        headerStyle="light"
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            To upgrade your plan and unlock more features, please contact Noticeboard support or your Noticeboard customer care representative.
          </Typography>
          <NoticeboardButton
            variant="primary"
            onClick={() => setIsUpgradeModalOpen(false)}
            fullWidth
          >
            Close
          </NoticeboardButton>
        </Box>
      </ActionModal>
      {isMobile && !hideHeader && teamTab === "members" && (
        <Fab
          color="primary"
          aria-label="Add member"
          onClick={handleOpenAddMember}
          sx={mobileFabSx}
        >
          <AddIcon sx={{ fontSize: 30 }} />
        </Fab>
      )}
    </>
  );

  if (hideHeader) {
    return <Box sx={{ height: "100%" }}>{content}</Box>;
  }

  return (
    <PageContainer sx={{ mt: 2 }}>
      <PageSection sx={{ mb: 2 }}>{content}</PageSection>
      <VideoPlayerModal
        open={selectedVideo !== null}
        onClose={() => setSelectedVideo(null)}
        videoPath={selectedVideo?.videoPath || ""}
        title={selectedVideo?.title || ""}
      />
    </PageContainer>
  );
};

export default ManageTeam;
